import axios from "axios";
import * as Sentry from "@sentry/react";
import { sanearUrl } from "./sentry-scrub";
import {
  CLAVE_PROXY,
  proxyYaComprobado,
  recordarProxy,
  esAbsoluta,
} from "./api-base";

axios.defaults.withCredentials = true;

// ── Memoria del fallback, por sesión de pestaña ──────────────────────────────
// Una vez que comprobamos que esta red no alcanza el backend directo, no tiene
// sentido volver a pagar el fallo en CADA request: se sale derecho por el
// proxy. En la Posta el fallo tarda ~118 ms (el firewall resetea), pero un
// firewall que descarta en silencio hace esperar el timeout completo, y ahí
// serían decenas de segundos por llamada.
//
// ⚠️ Esta decisión se MUDÓ a `helpers/api-base.js` (21-sep-2026, #752) y acá
// solo se importa. Vivía suelta en este archivo, así que la conocía axios y no
// la conocía la telemetría: `activity-log.js` seguía posteando contra la URL
// absoluta y sus eventos morían enteros, en silencio, en toda red que bloquea
// `backend.smardx.com`. Medido: 35 documentos generados y CERO eventos.

// ── Detección ANTICIPADA del bloqueo ─────────────────────────────────────────
// El reintento de más abajo es reactivo: el médico paga un fallo y recién ahí
// la sesión se cambia de camino. Esto lo adelanta: al cargar la app se sondea
// el backend directo, y si no contesta la sesión arranca ya sabiendo que tiene
// que salir por el proxy.
//
// POR QUÉ IMPORTA MÁS DE LO QUE PARECE: no podemos depender de que informática
// de cada institución nos habilite un dominio. Bloquean `backend.smardx.com`
// porque resuelve a AWS, categoría "cloud hosting", y dejan pasar
// `app.smardx.com` porque es Cloudflare. La app tiene que resolverlo sola.
//
// El costo para quien NO está bloqueado es cero en la práctica: la sonda sale
// al cargar la página y entre eso y que el médico termine de tipear su clave
// pasan segundos. Cuando el interceptor la espera, ya está resuelta.
//
// Tres reglas que no son cosméticas:
//  · `/health/` y nada más: es público, no lleva auth ni datos, y ya existe. Única
//    excepción (#1757): si `/health/` no contesta, se pregunta UNA vez a
//    `/api/user/csrf/` same-origin (también público y sin datos) para distinguir un
//    deploy en curso de un bloqueo de red; ver `viaSameOriginEnMantenimiento`.
//  · Falla ABIERTA. Si la sonda se cuelga, revienta o tarda, NO bloquea nada:
//    la promesa se resuelve igual y el reintento reactivo sigue de red. Un
//    detector que puede dejar sin app al médico es peor que no tenerlo.
//  · No corre si ya hay veredicto en esta pestaña (`sessionStorage`).
const SONDA_TIMEOUT_MS = 2500;

let veredictoRed = null;

// ⚠️ La sonda no puede distinguir "esta red no llega" de "llegué pero el
// navegador me tapó la respuesta". Hoy `/health/` manda
// `access-control-allow-origin: https://app.smardx.com` (verificado el
// 14-sep-2026) y por eso el veredicto es confiable. Si ese header se cayera, la
// sonda mandaría a TODOS los médicos por el proxy, en silencio, y con eso el
// tráfico clínico entero pasaría por Cloudflare — que es exactamente la
// decisión que se tomó NO tomar. Por eso cada veredicto de bloqueo deja rastro:
// un pico masivo se ve, en vez de descubrirse meses después.
// El beacon viaja RELATIVO a propósito: `/api/...` es same-origin, o sea sale
// por la Pages Function de Cloudflare — el mismo camino de rescate que acabamos
// de comprobar que hace falta. Mandarlo al backend directo sería pedir el aviso
// justo por la vía que este aviso denuncia como rota.
const RUTA_BEACON = "/api/medical_analysis/red/bloqueo/";

// Por qué existe además de Sentry: **Sentry nunca se activó en producción**. El
// bundle servido no lleva DSN y `SENTRY_DSN` tampoco está en el SSM del backend,
// así que `captureMessage` es un no-op y lo ha sido siempre. Durante meses el
// único sensor de "un médico abrió la app y su red la bloquea" descargó en un
// buzón que no existe, y por eso a Hugo Inzulza lo supimos por un DM dos días
// después, con el rescate ahí sin usarse (INC-2026-31). El aviso va a
// CloudWatch, que es el canal que efectivamente se mira.
const avisarAlBackend = (motivo) => {
  try {
    // `keepalive` para que sobreviva si el médico navega o recarga en el
    // momento; `credentials: omit` porque esto corre ANTES del login y no hay
    // nada que autenticar. El `.catch` vacío no es descuido: si la red tampoco
    // deja pasar este aviso, no hay nada que hacer al respecto y no puede
    // costarle el arranque al médico.
    fetch(RUTA_BEACON, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ motivo: String(motivo || "").slice(0, 120) }),
      credentials: "omit",
      cache: "no-store",
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* la telemetría nunca decide si el médico entra */
  }
};

const marcarBloqueo = (motivo) => {
  recordarProxy(true);
  avisarAlBackend(motivo);
  try {
    Sentry.captureMessage("sonda de arranque: backend directo inalcanzable", {
      level: "info",
      tags: { source: "sonda-red" },
      extra: { motivo },
    });
  } catch {
    /* la telemetría nunca decide si el médico entra */
  }
};

// `/api/user/csrf/` y no `/health/`: la Pages Function solo cubre `/api/*` y
// `/health/` same-origin devuelve el index.html de la SPA con un 200. Es un GET
// público, sin auth ni datos. Techo corto y propio: se SUMA al de la sonda
// directa, y los requests de la app esperan el veredicto; un firewall que
// descarta en silencio no puede costarle al médico el doble de espera (test
// "una sonda que nunca vuelve").
const SONDA_MANTENIMIENTO_TIMEOUT_MS = 1000;
const viaSameOriginEnMantenimiento = async () => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), SONDA_MANTENIMIENTO_TIMEOUT_MS);
  try {
    const r = await fetch("/api/user/csrf/", {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      signal: ctrl.signal,
    });
    return esMantenimientoRespuesta(r.status, r.headers.get("content-type"));
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
};

const sondearCaminoDirecto = async () => {
  const base = String(process.env.REACT_APP_BACKEND_URI || "").replace(/\/+$/, "");
  if (!base) return;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), SONDA_TIMEOUT_MS);
  try {
    const r = await fetch(`${base}/health/`, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      signal: ctrl.signal,
    });
    // Un 403 del filtro de la red también es "no llego": el health nuestro
    // responde 200 JSON. Cualquier otra cosa se trata como bloqueo.
    const tipo = String(r.headers.get("content-type") || "");
    if (r.ok && tipo.includes("json")) return;
    marcarBloqueo(`http ${r.status} ${tipo || "(sin content-type)"}`);
  } catch (e) {
    // Sin respuesta (firewall que descarta, DNS, timeout, o CORS tapando la
    // respuesta): mismo veredicto, no se puede distinguir desde el navegador.
    //
    // Salvo durante un deploy (#1757, R16): el 502/503 de nginx/ALB no trae CORS
    // y también cae acá. Antes de culpar a la red se pregunta por la vía
    // same-origin: si ESA contesta 502/503 legible, es SmartDx actualizándose, no
    // un firewall. Marcarlo clavaba la pestaña en el proxy durante toda su vida
    // (tráfico clínico por Cloudflare, lo que esta sonda existe para evitar) y
    // mandaba un RED_BLOQUEADA falso. Falla ABIERTA: cualquier otra cosa (timeout,
    // excepción, 200, 502 en JSON) marca como siempre.
    // Cubre R16 — .claude/specs/1757-login-mantenimiento/
    if (await viaSameOriginEnMantenimiento()) {
      // v2 (opción C): además de no culpar a la red, se avisa al banner global para que
      // quien abre la app en pleno deploy lo vea (R16, R3).
      emitirMantenimiento();
      return;
    }
    marcarBloqueo(`sin respuesta: ${e?.name || "Error"}`);
  } finally {
    clearTimeout(t);
  }
};

/** Arranca la sonda una sola vez por pestaña. La llama `index.js` al cargar. */
export const detectarCaminoAlBackend = () => {
  if (veredictoRed) return veredictoRed;
  if (proxyYaComprobado()) {
    veredictoRed = Promise.resolve();
    return veredictoRed;
  }
  veredictoRed = sondearCaminoDirecto().catch(() => {});
  return veredictoRed;
};

/** Los requests esperan el veredicto si sigue en vuelo. Nunca rechaza. */
const esperarVeredicto = () => veredictoRed || Promise.resolve();

// ── El agujero que hacía decorativo al rescate ───────────────────────────────
// Unos 40 call-sites arman la URL a mano: `${REACT_APP_BACKEND_URI}api/...`.
// Axios, cuando el `url` ya es absoluto, IGNORA el `baseURL` por completo
// (`buildFullPath`): reescribir `baseURL` no los movía ni un milímetro, y el
// reintento llegaba a reintentar contra la misma URL bloqueada. Para un médico
// con la red cortada eso deja muertas la Ficha, el Buscador, los presets de
// Indicaciones y el admin de keywords, aunque el login sí lo rescataran.
//
// ⚠️ Esto vale SOLO para `config.url` (ver `aRelativa`): una URL absoluta a un
// dominio ajeno no se toca nunca. El `config.baseURL` SÍ se reescribe a "/"
// para cualquier absoluta, que es deuda declarada y no un descuido: el intento
// de cerrarlo apagaba el rescate entero. El porqué está donde se decide, más
// abajo en `eraAbsoluta`, y el test que lo describe se llama "DEUDA DECLARADA".
const origenBackend = () => {
  try {
    return new URL(process.env.REACT_APP_BACKEND_URI || "").origin;
  } catch {
    return "";
  }
};


const aRelativa = (url) => {
  const base = origenBackend();
  const crudo = String(url || "");
  if (!base || !esAbsoluta(crudo)) return null;
  try {
    const u = new URL(crudo);
    if (u.origin !== base) return null;
    // La Pages Function existe SOLO para `/api/*`. Fuera de ahí, same-origin
    // devuelve el index.html de la SPA con un 200: el caller se comería HTML
    // creyendo que le fue bien. Medido contra prod: `app.smardx.com/health/`
    // devuelve 200 text/html (un `/static/` que no existe da 404 desde #1717).
    if (!u.pathname.startsWith("/api/")) return null;
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }
};

// ── Bloqueo de red que CONTESTA en vez de cortar ─────────────────────────────
// El reintento de abajo nació para firewalls que DESCARTAN el paquete: eso
// produce `ERR_NETWORK` y no hay respuesta. Pero hay appliances corporativos
// que contestan: devuelven su propia página de bloqueo con un 403. Como eso es
// una respuesta HTTP válida, el reintento no se disparaba y el médico quedaba
// afuera teniendo el camino de rescate disponible y sin usar (Clínica Dávila
// Vespucio, 10-sep-2026: la pantalla de login le cargaba —o sea `app.smardx.com`
// pasaba— y la API le devolvía 403).
//
// Cómo se distingue de un 403 LEGÍTIMO del backend, que NO hay que reintentar
// (`HasTurnoAccess` denegado, por ejemplo): nuestra API es DRF y siempre
// contesta JSON. Una página de bloqueo viene en `text/html`. `content-type` es
// CORS-safelisted, así que el navegador lo deja leer aunque el request sea
// cross-origin — no hace falta tocar el backend ni agregar headers.
//
// ⚠️ NO alcanza para reintentar un POST, y acá está escrito por qué: un 403
// prueba que el request no se procesó SOLO si el appliance corta a la ida. Un
// DLP con inspección TLS que revisa la RESPUESTA devuelve el mismo 403 HTML
// sobre un POST que el backend YA ejecutó, y desde el navegador los dos casos
// son idénticos. `ERR_NETWORK` sí garantiza que la conexión nunca se abrió: son
// garantías de distinta fuerza. Por eso el 403 rescata solo GET/HEAD/OPTIONS,
// igual que el timeout — duplicar una epicrisis en la ficha de un paciente real
// es un precio que no se paga por una comodidad.
//
// Y no hace falta para el caso que motivó esto: el login hace un GET a
// `/api/user/csrf/` ANTES del POST del token, así que ese GET se lleva el 403,
// rescata la sesión y el POST ya sale same-origin en el primer intento.
// 502/503 quedan FUERA de `esRespuestaAjena`: los produce nuestro propio nginx
// (o el ALB/CloudFront) cuando el backend se cae o se recrea en un deploy, y
// desviar la sesión entera por Cloudflare cuando el problema es nuestro metería
// tráfico clínico por una superficie que no corresponde. Tienen su propio
// detector, `esMantenimiento`, más abajo (#1757).
const STATUS_DE_BLOQUEO = new Set([403, 407, 451]);

export const esRespuestaAjena = (error) => {
  const res = error?.response;
  if (!res || !STATUS_DE_BLOQUEO.has(res.status)) return false;
  const tipo = String(
    res.headers?.["content-type"] || res.headers?.["Content-Type"] || ""
  ).toLowerCase();
  // Sin `content-type` tampoco es nuestro: DRF siempre lo manda.
  return !tipo.includes("json");
};

// ── Mantenimiento: el 502/503 de un deploy (#1757) ───────────────────────────
// Durante cada deploy (~1-2 min) el backend se recrea y quien contesta es nginx
// del EC2, el ALB o CloudFront: 502/503 con una página HTML. NUNCA DRF: nuestra
// API siempre contesta JSON. Por eso un 503 en JSON (el OCR ocupado, por
// ejemplo) NO es un deploy, y un 504 tampoco: lo emite el ALB/CloudFront cuando
// el backend TARDÓ, o sea el request pudo haberse procesado.
//
// Solo es "mantenimiento" una respuesta LEGIBLE. Por el camino directo el 502 de
// nginx no trae cabeceras CORS y el navegador lo entrega como ERR_NETWORK, igual
// que un firewall; lo que lo vuelve legible es el reintento por la vía
// same-origin (app.smardx.com/api/*), que devuelve el 502/503 con su status y su
// content-type. Un 500 del Worker (Cloudflare no alcanza al backend) tampoco
// cuenta: sigue siendo "server".
// Cubre R1, R2 — .claude/specs/1757-login-mantenimiento/
export const STATUS_MANTENIMIENTO = new Set([502, 503]);

export const esMantenimientoRespuesta = (status, contentType) =>
  STATUS_MANTENIMIENTO.has(Number(status)) &&
  !String(contentType || "").toLowerCase().includes("json");

export const esMantenimiento = (error) => {
  const res = error?.response;
  if (!res) return false;
  return esMantenimientoRespuesta(
    res.status,
    res.headers?.["content-type"] || res.headers?.["Content-Type"] || ""
  );
};

// ── Señales del banner global de mantenimiento (#1757 v2, opción C) ─────────
// get-client NO importa al banner ni a su store: les habla por eventos de `window`, igual
// que `smartdx:usage-limit` y `smartdx:acceso-bloqueado`. El interceptor es el único lugar
// que ve TODO 502/503 de la app (el reintento same-origin vuelve a pasar por él), así que
// una llamada de login, del Turno o de guardar emiten la misma señal. Emitirla jamás cambia
// lo que recibe quien llamó (R4): el error se rechaza igual y el autosave reintenta como hoy.
// `helpers/mantenimiento.js` decide si el banner sale (R5) y cuándo se va (R8).
// Cubre R3, R4, R12, R16 — .claude/specs/1757-login-mantenimiento/
export const SENAL_MANTENIMIENTO = "smartdx:mantenimiento";
export const SENAL_BACKEND_OK = "smartdx:backend-ok";
const emitirSenal = (nombre) => {
  try {
    window.dispatchEvent(new CustomEvent(nombre));
  } catch {
    /* la señal es un aviso: si no se puede emitir, la llamada sigue igual */
  }
};
const emitirMantenimiento = () => emitirSenal(SENAL_MANTENIMIENTO);

// ── Actividad contra el backend ──────────────────────────────────────────────
// La usa el cierre por inactividad (useIdleLogout) para no sacar a un médico
// que SÍ está trabajando aunque no toque el mouse: un dictado largo, una
// generación del LLM, un autoguardado del turno.
//
// Solo cuentan los métodos que ESCRIBEN. Un GET no cuenta a propósito: la app
// tiene pollers que consultan estado en loop, y si contaran, el timeout de
// inactividad no se cumpliría nunca y la protección sería decorativa.
const METODOS_QUE_ESCRIBEN = new Set(["post", "put", "patch", "delete"]);
let ultimaEscritura = 0;
// Cuándo arrancó cada escritura que sigue en vuelo, no cuántas hay. El contador
// pelado solo bajaba en el interceptor de RESPUESTA, y el cliente general no
// tiene timeout a propósito (una generación del LLM tarda minutos): una sola
// promesa que nunca resuelve —un firewall de hospital que descarta en silencio—
// dejaba "hay trabajo en curso" en true para siempre, y con eso el cierre por
// inactividad apagado en esa pestaña, sin ninguna señal.
let escriturasEnVuelo = [];
// Techo generoso: la generación más lenta medida ronda los 3 min. Pasado esto,
// la escritura ya no cuenta como trabajo aunque la promesa siga viva.
const VUELO_MAXIMO_MS = 10 * 60 * 1000;

const enVueloVigentes = () => {
  const corte = Date.now() - VUELO_MAXIMO_MS;
  escriturasEnVuelo = escriturasEnVuelo.filter((t) => t > corte);
  return escriturasEnVuelo.length;
};

const esEscritura = (config) =>
  METODOS_QUE_ESCRIBEN.has(String(config?.method || "get").toLowerCase());

// ── Consumo de API (barra de uso semanal, #1209) ─────────────────────────────
// Endpoints que le cuestan plata al usuario: al terminar uno con éxito, la
// barra del navbar tiene que volver a preguntar. Va acá y no en cada caller por
// la misma razón que el 429 de arriba: el gasto sale de varios lugares
// (generar, generar async, correr el turno, transcribir) y ninguno debería
// tener que acordarse de avisar. Lo escucha `useWeeklyUsage`, que además
// agrupa las ráfagas: "Generar turno" dispara una llamada POR CAMA.
const ENDPOINTS_QUE_CONSUMEN =
  /\/(llm\/generate|turno\/run|transcribe|asr\/realtime-token)/;

const avisarConsumo = (config) => {
  try {
    if (!esEscritura(config)) return;
    if (!ENDPOINTS_QUE_CONSUMEN.test(String(config?.url || ""))) return;
    window.dispatchEvent(new Event("smartdx:usage-changed"));
  } catch {
    /* la barra es informativa: avisar nunca puede romper el request */
  }
};

const marcarTrabajo = (config) => {
  if (!esEscritura(config)) return;
  ultimaEscritura = Date.now();
  escriturasEnVuelo.push(Date.now());
};

const terminarTrabajo = (config) => {
  if (!config || !esEscritura(config)) return;
  ultimaEscritura = Date.now();
  escriturasEnVuelo.shift();
};

/** { enVuelo, desdeUltima } — ms desde la última escritura, Infinity si nunca. */
export const actividadBackend = () => ({
  enVuelo: enVueloVigentes(),
  desdeUltima: ultimaEscritura ? Date.now() - ultimaEscritura : Infinity,
});

const getClient = (options = {}) => {
  const baseURL =
    typeof options === "string"
      ? options
      : options?.baseURL || process.env.REACT_APP_BACKEND_URI;
  const client = axios.create({
    baseURL,
  });

  // Read token from localStorage on every request (not just at client creation)
  // so stale axios instances from previous renders don't send outdated tokens.
  client.interceptors.request.use(async (config) => {
    // ⚠️ `marcarTrabajo` va ANTES de cualquier `await`, y eso no es estilo.
    // Al volver async este interceptor, moverlo después del await abría una
    // ventana en la que una escritura en curso NO contaba como trabajo, y de
    // eso depende el cierre por inactividad: se podía sacar por inactivo a un
    // médico que estaba guardando. Lo agarró el test de la escritura colgada.
    marcarTrabajo(config);
    // Si la sonda de arranque sigue en vuelo, se la espera: sin esto el primer
    // request de un médico bloqueado sale igual por el camino malo y paga el
    // fallo, que es justo lo que la sonda viene a evitar. Nunca rechaza y tiene
    // su propio techo, así que no puede colgar la app.
    await esperarVeredicto();
    // Instante en que el request SALE (no en que vuelve): lo lleva el evento
    // `smartdx:acceso-bloqueado` para que PausaInactividadGate ordene un 403 contra
    // la última lectura de /me (#1583 R27, ronda 2). Sin esto, un 403 de un
    // request que salió antes de esa lectura llegaba después y re-bloqueaba.
    config.__enviadoEn = typeof performance !== "undefined" ? performance.now() : Date.now();
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Token ${authToken}`;
    } else if (config.headers) {
      delete config.headers["Authorization"];
    }

    // Si en esta sesión ya se comprobó que el backend directo no es alcanzable,
    // salimos por el proxy desde el vamos en vez de fallar y reintentar. Va acá
    // y no en la creación del cliente porque los clientes se crean al montar
    // los componentes, o sea antes de que sepamos que la red está bloqueada.
    if (proxyYaComprobado()) {
      if (esAbsoluta(config.baseURL)) config.baseURL = "/";
      const relativa = aRelativa(config.url);
      if (relativa) config.url = relativa;
    }
    return config;
  });

  // Reporta a Sentry los 5xx y errores de red. No toca la cadena de error ni
  // la lógica de auth (401/403 quedan para una entrega posterior una vez que
  // tengamos datos reales del comportamiento en prod).
  client.interceptors.response.use(
    (response) => {
      terminarTrabajo(response.config);
      avisarConsumo(response.config);
      emitirSenal(SENAL_BACKEND_OK);
      return response;
    },
    (error) => {
      terminarTrabajo(error?.config);
      // 502/503 de un deploy (R3): una señal, sin tocar el error (R4). El 502 sin CORS del
      // camino directo no es legible acá (es ERR_NETWORK); la señal sale del reintento
      // same-origin que lo trae legible y que pasa por este mismo interceptor.
      if (esMantenimiento(error)) emitirMantenimiento();
      try {
        const status = error?.response?.status;
        // Tope diario de uso alcanzado (#185/#735/#777). Se emite un evento
        // global en vez de dejar que cada caller lo muestre como "no pudimos
        // generar": el médico tiene que entender que no es una falla del
        // sistema. Lo escucha UsageLimitDialog, montado una sola vez.
        // Va acá y no en cada caller porque el 429 puede venir de varios
        // endpoints (generar, generar async, token de dictado, transcribir).
        if (status === 429) {
          const detail =
            error?.response?.data?.error ||
            error?.response?.data?.detail ||
            "";
          if (/l[íi]mite diario/i.test(String(detail))) {
            window.dispatchEvent(
              new CustomEvent("smartdx:usage-limit", { detail: String(detail) }),
            );
          }
        }
        // Pago vencido o prueba vencida a mitad de sesión (#862, R32): el
        // `/me/` del arranque decía que se podía entrar, y venció en el
        // medio. `billing.exceptions.handler_con_code` agrega `code` al
        // cuerpo de CUALQUIER 403 que declare uno (R27); acá solo se emite
        // el evento cuando el `code` viaja, y es SuscripcionGate.js quien
        // decide si le importa (solo reacciona a `trial_expired` y
        // `subscription_past_due`, no a un módulo sin habilitar). Mismo
        // patrón que el 429 de arriba: un solo lugar, no cablearlo en cada
        // caller.
        //
        // CONVIVE con el bloque de `esRespuestaAjena` de abajo y no se pisan:
        // este exige que el cuerpo sea JSON (para que exista `data.code`) y
        // aquél exige que el content-type NO sea JSON. Un 403 de billing
        // nunca cae en esa rama ni al revés (acordado con la sesión de
        // INC-2026-31, que escribió el otro lado).
        if (status === 403) {
          const code = error?.response?.data?.code || "";
          if (code) {
            window.dispatchEvent(
              new CustomEvent("smartdx:acceso-bloqueado", {
                detail: { code, enviadoEn: error?.config?.__enviadoEn },
              }),
            );
          }
        }
        // Un 403 de un appliance no llegaba NUNCA a Sentry (solo se reportaban
        // 5xx y errores sin respuesta), así que el bloqueo de una red era
        // invisible del lado nuestro y el médico era el único sensor. Se manda
        // con el content-type, que es lo que decide si el rescate dispara.
        if (esRespuestaAjena(error)) {
          Sentry.captureMessage("bloqueo de red: respuesta ajena", {
            level: "warning",
            tags: { source: "axios", status: String(status) },
            extra: {
              // `sanearUrl` acá y no solo el `beforeSend` global: el resto del
              // archivo aplica las dos capas y esta línea era la única que
              // dependía de una sola. Una URL de bloqueo puede llevar el RUT en
              // el query string.
              url: sanearUrl(error?.config?.url),
              baseURL: error?.config?.baseURL,
              contentType: String(
                error?.response?.headers?.["content-type"] || "(sin content-type)"
              ),
              metodo: error?.config?.method,
            },
          });
        }
        if (status >= 500 || !error?.response) {
          Sentry.captureException(error, {
            tags: {
              source: "axios",
              status: status ? String(status) : "network",
              method: error?.config?.method,
            },
            // Sin query: `paciente-historico/?rut=` y `search-patient/?q=`
            // llevan al paciente en el parámetro (P0-5, auditoría 21.719).
            extra: {
              url: sanearUrl(error?.config?.url),
              baseURL: error?.config?.baseURL,
            },
          });
        }
      } catch {
        // No bloquear la cadena de error original si Sentry falla.
      }

      // ── Reintento automático por el proxy same-origin ────────────────────
      // Si el request murió SIN respuesta, reintentamos una vez contra
      // app.smardx.com/api/*, que lo proxea la Pages Function de Cloudflare
      // (functions/api/[[path]].js).
      //
      // POR QUÉ: hay redes de hospital que dejan pasar Cloudflare (donde vive
      // el front) y bloquean el destino del backend. La médica veía cargar la
      // pantalla de login y al entrar le decía que no se pudo conectar.
      // Barros Luco 14-ago, Posta Central 15-ago, INC-2026-16.
      //
      // La alternativa era mandar a TODOS por el proxy (poner
      // REACT_APP_BACKEND_URI=/). No se hizo: eso metería el tráfico clínico de
      // todos los médicos por un Worker de Cloudflare, o sea una superficie
      // nueva bajo la Ley 21.719, para resolver el problema de algunos. Así
      // solo pasa por ahí quien no tiene otra forma de llegar.
      //
      // ⚠️ EL TIMEOUT ES EL CASO PELIGROSO. Un timeout significa que el request
      // PUDO haber llegado y haberse procesado; reintentarlo a ciegas duplicaría
      // una pestaña de turno o una epicrisis en la ficha de un paciente real.
      // Por eso el disparador normal es solo `ERR_NETWORK`, que es el navegador
      // diciendo que no logró ni abrir la conexión: del otro lado no se ejecutó
      // nada.
      //
      // La excepción son los métodos idempotentes (GET/HEAD/OPTIONS): repetirlos
      // no cambia nada del lado del servidor, así que ahí sí se reintenta por
      // timeout. Importa porque un firewall que DESCARTA EN SILENCIO en vez de
      // resetear no produce ERR_NETWORK sino timeout, y sin esta rama el médico
      // detrás de ese firewall se quedaba afuera igual que antes del arreglo.
      try {
        const cfg = error?.config;
        const sinRespuesta = !error?.response;
        const falloDeRed = error?.code === "ERR_NETWORK" && sinRespuesta;
        const esTimeout =
          (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") &&
          sinRespuesta;
        const metodo = String(cfg?.method || "get").toLowerCase();
        const esIdempotente = ["get", "head", "options"].includes(metodo);

        // Un bloqueo que contesta (403 de un appliance) no produce
        // ERR_NETWORK, así que necesita su propia rama o el rescate no corre.
        // Va junto al timeout y no junto a ERR_NETWORK: ninguno de los dos
        // prueba que del otro lado no se haya ejecutado nada.
        const bloqueoAjeno = esRespuestaAjena(error);
        const disparar =
          falloDeRed || ((esTimeout || bloqueoAjeno) && esIdempotente);
        const yaReintentado = cfg?.__smartdxProxyRetry;
        // Si el request ya salía relativo, este reintento no aportaría nada.
        // Cuenta tanto el `baseURL` absoluto como el `url` absoluto a mano.
        // OJO: `esAbsoluta` y no una comparación contra `REACT_APP_BACKEND_URI`.
        // Se intentó lo segundo para que un cliente creado contra un tercero no
        // terminara mandando su payload a app.smardx.com, y **apagaba el rescate
        // entero** para todo cliente cuyo `baseURL` no fuera exactamente la env
        // (lo destapó `get-client.sentry.test.js`, que usa un host de prueba).
        // Romper el mecanismo vivo para cerrar una mina que hoy no existe es mal
        // negocio: al 14-sep-2026 `REACT_APP_LLM_BACKEND_URI` apunta al mismo
        // backend, así que no hay ningún cliente contra un tercero. Queda como
        // deuda declarada, no como arreglo a medias.
        const eraAbsoluta =
          esAbsoluta(cfg?.baseURL) || Boolean(aRelativa(cfg?.url));

        if (disparar && cfg && !yaReintentado && eraAbsoluta) {
          cfg.__smartdxProxyRetry = true;
          cfg.baseURL = "/";
          const relativa = aRelativa(cfg.url);
          if (relativa) cfg.url = relativa;
          // También por el beacon, no solo por Sentry: este es el evento MÁS
          // fuerte de todos —el rescate disparando de verdad— y hasta hoy era
          // el que más silenciosamente se perdía. Se ve igual en los access
          // logs de CloudFront por la firma del XFF, pero eso exige que alguien
          // vaya a buscarlo; esto llega solo a CloudWatch.
          avisarAlBackend(
            falloDeRed
              ? "sin respuesta: ERR_NETWORK"
              : bloqueoAjeno
                ? `http ${error?.response?.status} (sin content-type)`
                : "sin respuesta: Timeout",
          );
          try {
            Sentry.captureMessage("fallback same-origin activado", {
              level: "info",
              tags: {
                source: "axios",
                reason: falloDeRed
                  ? "ERR_NETWORK"
                  : bloqueoAjeno
                    ? `BLOQUEO_${error?.response?.status}`
                    : "TIMEOUT_IDEMPOTENTE",
              },
              extra: { url: sanearUrl(cfg.url), baseURLOriginal: error?.config?.baseURL },
            });
          } catch {
            /* la telemetría nunca bloquea el reintento */
          }
          // Se recuerda si el reintento obtuvo CUALQUIER respuesta HTTP, no solo
          // un 2xx: un 500 que vuelve por el proxy igual demuestra que ese
          // camino llega al backend, que es lo único que la memoria afirma.
          // Recordarlo antes de saberlo mandaría la sesión entera por un camino
          // que quizá tampoco sirve.
          //
          // Nota honesta: se comprobó por mutación que adelantar este
          // `recordarProxy` a antes del reintento NO cambia el comportamiento
          // observable, porque la rama de olvido de más abajo lo limpia. Se
          // deja en el orden correcto igual: si algún día se saca esa rama, el
          // orden pasa a importar y no hay test que lo agarre.
          return client.request(cfg).then(
            (res) => {
              recordarProxy(true);
              return res;
            },
            (err) => {
              // Un 500 que vuelve por el proxy igual demuestra que ese camino
              // llega al backend. Pero si el proxy devuelve OTRA página de
              // bloqueo, esa red corta los dos caminos y recordarlo mandaría la
              // sesión entera por una vía que tampoco sirve.
              // Cubre R15 — .claude/specs/1757-login-mantenimiento/: el 502/503
              // de un deploy cae en este mismo `st < 500` y tampoco se recuerda
              // (test en get-client.fallback.test.js).
              // Los 5xx quedan fuera a propósito: la Pages Function no tiene
              // try/catch, así que un backend inalcanzable DESDE Cloudflare
              // sale como 500 del Worker. Eso es una respuesta HTTP, y
              // recordarlo clavaba la pestaña en un camino muerto del que la
              // rama de olvido no la saca (solo mira fallos de red).
              const st = err?.response?.status || 0;
              if (err?.response && st < 500 && !esRespuestaAjena(err)) {
                recordarProxy(true);
              }
              throw err;
            }
          );
        }

        // El proxy dejó de servir: se olvida, así la sesión vuelve a probar el
        // camino directo en vez de quedar clavada en uno que ya no anda.
        if ((falloDeRed || esTimeout) && !eraAbsoluta && proxyYaComprobado()) {
          recordarProxy(false);
        }
      } catch {
        // Si el reintento falla al armarse, devolvemos el error original.
      }

      return Promise.reject(error);
    }
  );

  return client;
};

export default getClient;
